import { triggerTracking } from './triggerTracking';
import { trackPage as trackSegmentPage } from './segment/trackPage';
import { Ecommerce, EventNames, Taxonomy } from './contracts';
import { getPageTrackingData } from './getPageTrackingData/getPageTrackingData';
import { getUserTaxonomy } from './getUserTaxonomy';
import { trackSegmentAndGAEvent } from './trackSegmentAndGAEvent';
import { removePropertiesWithNullishValues } from './helpers';
import { pLogger } from '@zoopla/logger';

type PageTrackingData = {
    ecommerce?: Record<string, unknown> | undefined;
    taxonomy: Taxonomy;
};

export const trackPage = async (
    page: string,
    extraTaxonomy: Record<string, unknown> = {},
    extraEcommerce: Record<string, unknown> | null = null,
    abortSignal?: AbortSignal
): Promise<PageTrackingData | null> => {
    try {
        const userTaxonomy = removePropertiesWithNullishValues(
            await getUserTaxonomy(abortSignal)
        );

        const pageTrackingData = getPageTrackingData(
            page,
            extraTaxonomy,
            extraEcommerce
        );

        const pageShowEvent = {
            event: 'pageshow',
            ...pageTrackingData,
            taxonomy: {
                ...pageTrackingData.taxonomy,
                ...userTaxonomy
            }
        };

        triggerTracking(pageShowEvent);

        await trackSegmentPage(pageTrackingData.taxonomy);

        return pageTrackingData;
    } catch (e) {
        const logger = pLogger.child({
            page,
            custodian: 'platform'
        });

        // TODO: use new O11y client logging
        logger.error(e);
        return null;
    }
};

/**
 * @param  {string} page the page value to be used in trackSegmentAndGAEvent
 * @param  {string} virtualPageIdentifier the identifier for that particular
 *    "virtual" page e.g. a form step name
 * @param  {Taxonomy} taxonomy?
 * @param  {Ecommerce} ecommerce?
 */
export const trackVirtualPage = async (
    page: string,
    virtualPageIdentifier: string,
    taxonomy?: Taxonomy,
    ecommerce?: Ecommerce
) => {
    trackSegmentAndGAEvent(
        EventNames.VIEWED_VIRTUAL_PAGE,
        {},
        page,
        // Tracking is due an overhaul, so rather than refactoring this casting,
        // it has been ignored in the short term.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {
            ...taxonomy,
            virtualPageIdentifier
        } as Taxonomy,
        ecommerce
    );
};
